import { NextPage } from "next";
import { useState } from "react";

import { ssrMode } from "@app/constants";
import { structuredDataAggregateReviews } from "@app/core/SEO/Product/structuredData";
import { ProductListPage } from "@components/pages/ProductList/ProductListPage";
import { Attribute } from "@graphql/gqlTypes/Attribute";
import { useAttributesFiltering } from "@hooks/useAttributesFiltering";
import useProductFiltering from "@hooks/useProductFiltering";
import { useProductLoading } from "@hooks/useProductLoading";
import { getDesignerFromSlug } from "@utils/designers";
import { PriceFilterType } from "@utils/filterData";
import { FeaturedProducts, PageTypes, parseMetaFilterValue } from "@utils/ssr";

import { MetaWrapper, NotFound } from "../../components/legacy";

interface AttributedProductsData {
  designer: any;
  article: any;
  ancestors: any;
  attributes: Attribute[];
  featuredProducts: FeaturedProducts;
  products: any; // SSR Products Query Result
  presetPriceFilters: PriceFilterType;
}

type AttributedProductsViewProps = {
  params: { slug: string } | undefined;
  data:
    | ({ slug?: string; locationData: any[] } & AttributedProductsData)
    | undefined
    | null;
  presetAttributeFilter: { [key: string]: string[] };
  categoryFilter: { [key: string]: string[] };
  presetPriceFilter?: PriceFilterType;
};

export const AttributedProductsView: NextPage<AttributedProductsViewProps> = ({
  data: attributedProducts,
  presetAttributeFilter,
  categoryFilter,
}) => {
  const attributes = useAttributesFiltering({});
  const presetPriceFilters = attributedProducts?.presetPriceFilters;
  const disablePriceFiltering =
    !!presetPriceFilters?.lte || !!presetPriceFilters?.gte;

  const isDesignerPage =
    attributedProducts?.article?.pageType?.slug === PageTypes.DESIGNER;
  const additionalFilter: any =
    presetAttributeFilter ||
    (isDesignerPage
      ? {
          designer: [getDesignerFromSlug(attributedProducts?.article.slug)],
        }
      : {});
  delete additionalFilter.priceGte;
  delete additionalFilter.priceLte;

  let locationData = [];
  const isLocationPage =
    attributedProducts?.article?.pageType?.slug === PageTypes.LOCATION;
  if (isLocationPage) {
    const postcodes = attributedProducts.article.metadata.find(
      (x) => x.key === "postcodes"
    )?.value;
    locationData = postcodes ? parseMetaFilterValue(postcodes) : [];
  }

  const {
    clientFiltered,
    filters,
    pricesFilter,
    locationFilter,
    handleClearFilters,
    handleFiltersChange,
    handlePricesFilter,
    handleLocationFilter,
    handleDeliveryFilter,
    deliveryFilter,
    hasDeliveryFilter,
    handleOrderChange,
    keyword,
    setKeyword,
  } = useProductFiltering({
    presetAttributeFilter: additionalFilter,
    presetLocationFilter: locationData,
    presetPriceFilters,
    categoryFilter,
  });

  // For non-filter pages, we need to get the default-sort directly from metadata
  const defaultSortMetadata = attributedProducts?.article?.metadata?.find(
    (x) => x.key === "default-sort"
  );

  filters.attributes = { ...filters.attributes, ...additionalFilter };

  const filteredAttributes =
    attributes?.filter((x) => x.slug && !(x.slug in additionalFilter)) || [];

  const sortValue =
    filters.attributes["default-sort"]?.at(0) ||
    defaultSortMetadata?.value ||
    "best-match";

  const extractedArray = sortValue?.match(/'([^']+)'/);

  const defaultSort = extractedArray ? extractedArray[1] : sortValue;

  const [activeSortOption, setActiveSortOption] = useState<string | null>(
    () => defaultSort
  );

  const mapSortToBackend = (sort: string | null): string | null => {
    switch (sort) {
      case "price-lowest":
        return "price";
      case "price-highest":
        return "-price";
      case "oldest":
        return "created_at";
      case "newest":
        return "-created_at";
      case "best-match":
        return null; // Backend expects `null` for best match
      default:
        return sort; // Fallback in case of unexpected values
    }
  };

  filters.sortBy = mapSortToBackend(activeSortOption) ?? filters.sortBy;

  const onSortChange = (newSort: string | null) => {
    setActiveSortOption(newSort); // Update user-facing state
    handleOrderChange({
      value: newSort,
    });
  };

  const {
    products,
    loading,
    pageInfo,
    numberOfProducts,
    handleLoadMore,
    loadMoreError,
  } = useProductLoading({
    ssrData: attributedProducts?.products?.products,
    ids: { categoryId: "" },
    filters,
    hasDeliveryFilter,
    clientFiltered,
    postcodes: locationData,
  });

  return attributedProducts ? (
    <MetaWrapper
      meta={{
        description: attributedProducts.article.seoDescription,
        title: attributedProducts.article.seoTitle,
        type: "product.attributedProducts",
        image: products?.length && products[0].thumbnail2x?.url,
        url: ssrMode ? global.location.href : window.location.href,
      }}
    >
      {isLocationPage && (
        <script
          className="structured-data-list"
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: structuredDataAggregateReviews(),
          }}
        />
      )}
      <ProductListPage
        title={attributedProducts.article.title}
        seoDescription={attributedProducts.article.seoDescription}
        textContent={attributedProducts.article.content}
        attributes={filteredAttributes}
        clearFilters={handleClearFilters}
        products={products}
        displayLoader={!products.length && loading}
        hasNextPage={!!pageInfo?.hasNextPage}
        numberOfProducts={numberOfProducts}
        activeSortOption={filters.sortBy}
        filters={filters}
        onAttributeFiltersChange={handleFiltersChange}
        onLoadMore={handleLoadMore}
        activeFilters={
          Object.keys(filters?.attributes || { undefined }).length -
          Object.keys(additionalFilter).length
        }
        onOrder={(option) => onSortChange(option?.value || null)}
        pricesFilter={pricesFilter}
        setPricesFilter={handlePricesFilter}
        locationFilter={locationFilter}
        setLocationFilter={handleLocationFilter}
        disablePriceFiltering={disablePriceFiltering}
        disableLocationFiltering={isLocationPage}
        deliveryFilter={deliveryFilter}
        setDeliveryFilter={handleDeliveryFilter}
        loadMoreError={loadMoreError}
        keyword={keyword}
        setKeyword={setKeyword}
        pageType={attributedProducts?.article?.pageType}
        isFetching={loading}
        aboveProductContent={attributedProducts?.article?.aboveProductContent}
      />
    </MetaWrapper>
  ) : (
    <NotFound />
  );
};
